/**
 * EventItemCard — rollen-aware Item-Card.
 *
 * Layout ist für alle Rollen identisch (Pill oben, Name groß, Comment unten,
 * Aktion rechts). Was je Rolle variiert: Drag-Handle, Pill-Verhalten,
 * Action-Menü, Claim-Button, eigener Kommentar.
 */

import { useEffect, useRef, useState } from "react";
import {
  CheckCircle,
  CopySimple,
  DotsSixVertical,
  PencilSimple,
  Trash,
  UserPlus,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { ActionMenu, type Action } from "@/components/ActionMenu";
import { Button } from "@/components/Button";
import { useConfirm } from "@/components/Confirm";
import { InlineEdit } from "@/components/InlineEdit";
import { Input } from "@/components/Input";
import { avatarColor, initials } from "@/lib/avatar";
import type { GuestItem, Item, Participant } from "@/lib/api";
import styles from "./EventItemCard.module.css";

type AdminCallbacks = {
  onEdit: (id: number, payload: { name?: string; comment?: string | null; forAllUsers?: boolean }) => void;
  onAssign: (id: number, participantId: number | null, forAll: boolean) => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
};

type UserCallbacks = {
  onClaim: (id: number) => void;
  onRelease: (id: number) => void;
  onSetUserComment: (id: number, comment: string | null) => void;
};

type AdminProps = {
  viewerRole: "admin";
  item: Item;
  participants: Participant[];
} & AdminCallbacks;

type UserProps = {
  viewerRole: "user";
  item: Item;
  participants: Participant[];
  selfId: number;
  selfName: string;
  disabled?: boolean;
} & UserCallbacks;

type GuestProps = {
  viewerRole: "guest";
  item: GuestItem;
  participants: Participant[];
};

export type EventItemCardProps = AdminProps | UserProps | GuestProps;

export function EventItemCard(props: EventItemCardProps) {
  if (props.viewerRole === "admin") return <AdminCard {...props} />;
  if (props.viewerRole === "user") return <UserCard {...props} />;
  return <GuestCard {...props} />;
}

// ---------------------------------------------------------------------------
// Admin-Variante: Drag, Assign-Pill, ⋯-Menu mit Inline-Edit/Duplicate/Delete.
// ---------------------------------------------------------------------------

function AdminCard({ item, participants, onEdit, onAssign, onDuplicate, onDelete }: AdminProps) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [assignOpen, setAssignOpen] = useState(false);
  const assignWrapRef = useRef<HTMLDivElement>(null);

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });
  const dragStyle = { transform: CSS.Transform.toString(transform), transition };

  useEffect(() => {
    if (!assignOpen) return;
    const handler = (e: MouseEvent) => {
      if (assignWrapRef.current && !assignWrapRef.current.contains(e.target as Node)) {
        setAssignOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [assignOpen]);

  const assignedParticipant = item.participantId
    ? participants.find((p) => p.id === item.participantId) ?? null
    : null;

  const handleDelete = async () => {
    const r = await confirm({
      title: t("admin.confirmDeleteItemTitle"),
      body: t("admin.confirmDeleteItemBody"),
      variant: "danger",
      confirmLabel: t("common.delete"),
    });
    if (r.confirmed) onDelete(item.id);
  };

  const actions: Action[] = [
    { key: "duplicate", icon: CopySimple, label: t("admin.duplicate"), onClick: () => onDuplicate(item.id) },
    { key: "delete", icon: Trash, label: t("common.delete"), onClick: handleDelete, variant: "danger" },
  ];

  const menuContent = (
    <>
      <InlineEdit
        label={t("admin.editNameLabel")}
        value={item.name}
        onSave={(next) => onEdit(item.id, { name: next })}
        maxLength={100}
        required
      />
      <InlineEdit
        label={t("admin.editCommentLabel")}
        value={item.comment ?? ""}
        onSave={(next) => onEdit(item.id, { comment: next || null })}
        multiline
        maxLength={300}
        emptyPlaceholder="—"
      />
    </>
  );

  return (
    <article
      ref={setNodeRef}
      style={dragStyle}
      className={`${styles.card} ${item.forAllUsers ? styles.cardHighlight : ""} ${
        item.deletedAt ? styles.cardDeleted : ""
      } ${isDragging ? styles.cardDragging : ""}`}
    >
      <div className={styles.header}>
        <button
          type="button"
          ref={setActivatorNodeRef}
          className={styles.dragHandle}
          aria-label={t("admin.dragHandleLabel", { name: item.name })}
          {...attributes}
          {...listeners}
        >
          <DotsSixVertical size={18} weight="bold" />
        </button>
        <div className={styles.body}>
          <div className={styles.assign} ref={assignWrapRef}>
            <AssignChip
              forAll={item.forAllUsers}
              assigned={assignedParticipant}
              onClick={() => setAssignOpen((v) => !v)}
              ariaExpanded={assignOpen}
            />
            {assignOpen && (
              <div className={styles.assignMenu} role="menu">
                <button
                  type="button"
                  className={styles.assignOption}
                  onClick={() => {
                    onAssign(item.id, null, true);
                    setAssignOpen(false);
                  }}
                >
                  <UsersThree size={18} weight="regular" />
                  {t("admin.assignForAll")}
                </button>
                {(assignedParticipant || item.forAllUsers) && (
                  <button
                    type="button"
                    className={styles.assignOption}
                    onClick={() => {
                      onAssign(item.id, null, false);
                      setAssignOpen(false);
                    }}
                  >
                    <X size={18} weight="regular" />
                    {t("admin.assignUnassigned")}
                  </button>
                )}
                {participants.length > 0 && <div className={styles.assignOptionDivider} />}
                {participants.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={styles.assignOption}
                    onClick={() => {
                      onAssign(item.id, p.id, false);
                      setAssignOpen(false);
                    }}
                  >
                    <span
                      className={styles.avatar}
                      style={{ backgroundColor: avatarColor(p.name) }}
                      aria-hidden
                    >
                      {initials(p.name)}
                    </span>
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className={styles.name}>{item.name}</span>
          {item.comment && <span className={styles.comment}>{item.comment}</span>}
        </div>
        <ActionMenu
          triggerLabel={t("common.actionsForLabel", { name: item.name })}
          title={t("common.actionsForLabel", { name: item.name })}
          closeLabel={t("common.close")}
          actions={actions}
          content={menuContent}
        />
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// User-Variante: Pill ist Anzeige, Action-Pill rechts (Claim/Release),
// eigener Kommentar bei isMine inline editierbar.
// ---------------------------------------------------------------------------

function UserCard({
  item,
  participants,
  selfId,
  disabled,
  onClaim,
  onRelease,
  onSetUserComment,
}: UserProps) {
  const { t } = useTranslation();
  const isMine = item.participantId === selfId;
  const assignedParticipant = item.participantId
    ? participants.find((p) => p.id === item.participantId) ?? null
    : null;

  const [draftComment, setDraftComment] = useState(item.userComment ?? "");
  const lastSyncedRef = useRef(item.userComment ?? "");
  useEffect(() => {
    const remote = item.userComment ?? "";
    if (draftComment === lastSyncedRef.current) setDraftComment(remote);
    lastSyncedRef.current = remote;
  }, [item.userComment, draftComment]);

  const trimmed = draftComment.trim();
  const canSave = trimmed !== (item.userComment ?? "").trim();
  const canClaim = !disabled && !item.forAllUsers && !assignedParticipant;

  return (
    <article
      className={`${styles.card} ${item.forAllUsers ? styles.cardHighlight : ""} ${
        isMine ? styles.cardMine : ""
      }`}
    >
      <div className={styles.header}>
        <div className={styles.body}>
          <span className={styles.assignReadonly}>
            <AssignDisplay
              forAll={item.forAllUsers}
              assigned={assignedParticipant}
            />
          </span>
          <span className={styles.name}>{item.name}</span>
          {item.comment && <span className={styles.comment}>{item.comment}</span>}
        </div>
        <div className={styles.userActions}>
          {canClaim && (
            <Button type="button" size="compact" onClick={() => onClaim(item.id)}>
              {t("event.claimMine")}
            </Button>
          )}
          {isMine && !disabled && (
            <Button
              type="button"
              size="compact"
              variant="ghost"
              onClick={() => onRelease(item.id)}
            >
              {t("event.releaseMine")}
            </Button>
          )}
        </div>
      </div>
      {isMine && !disabled && (
        <div className={styles.commentBox}>
          <Input
            label={t("event.yourComment")}
            value={draftComment}
            onChange={(e) => setDraftComment(e.target.value)}
            maxLength={300}
          />
          <Button
            type="button"
            size="compact"
            disabled={!canSave}
            onClick={() => onSetUserComment(item.id, trimmed || null)}
          >
            {t("event.saveComment")}
          </Button>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Guest-Variante: nur Anzeige, keine Aktion.
// ---------------------------------------------------------------------------

function GuestCard({ item, participants }: GuestProps) {
  const assignedParticipant = item.participantId
    ? participants.find((p) => p.id === item.participantId) ?? null
    : null;
  const claimed = item.claimed ?? (item.forAllUsers || item.participantId !== null);
  return (
    <article className={`${styles.card} ${item.forAllUsers ? styles.cardHighlight : ""}`}>
      <div className={styles.header}>
        <div className={styles.body}>
          <span className={styles.assignReadonly}>
            <AssignDisplay
              forAll={item.forAllUsers}
              assigned={assignedParticipant}
              claimedAnonymously={claimed && !item.forAllUsers && !assignedParticipant}
            />
          </span>
          <span className={styles.name}>{item.name}</span>
          {item.comment && <span className={styles.comment}>{item.comment}</span>}
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Pill-Komponenten (clickable Admin / readonly für User+Guest)
// ---------------------------------------------------------------------------

function AssignChip({
  forAll,
  assigned,
  onClick,
  ariaExpanded,
}: {
  forAll: boolean;
  assigned: Participant | null;
  onClick: () => void;
  ariaExpanded: boolean;
}) {
  const { t } = useTranslation();
  if (forAll) {
    return (
      <button
        type="button"
        className={`${styles.assignPill} ${styles.assignPillForAll}`}
        onClick={onClick}
        aria-haspopup="menu"
        aria-expanded={ariaExpanded}
      >
        <UsersThree size={14} weight="regular" />
        {t("event.claimedForAll")}
      </button>
    );
  }
  if (assigned) {
    return (
      <button
        type="button"
        className={`${styles.assignPill} ${styles.assignPillAssigned}`}
        onClick={onClick}
        aria-haspopup="menu"
        aria-expanded={ariaExpanded}
      >
        <span
          className={styles.avatar}
          style={{ backgroundColor: avatarColor(assigned.name) }}
          aria-hidden
        >
          {initials(assigned.name)}
        </span>
        {t("event.claimedBy", { name: assigned.name })}
      </button>
    );
  }
  return (
    <button
      type="button"
      className={styles.assignPill}
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={ariaExpanded}
    >
      <UserPlus size={14} weight="regular" />
      {t("admin.assignUnassignedCta")}
    </button>
  );
}

function AssignDisplay({
  forAll,
  assigned,
  claimedAnonymously = false,
}: {
  forAll: boolean;
  assigned: Participant | null;
  /** Position ist vergeben, aber der Name wird verborgen (`anonymous`-Event). */
  claimedAnonymously?: boolean;
}) {
  const { t } = useTranslation();
  if (forAll) {
    return (
      <span className={`${styles.assignReadPill} ${styles.assignReadPillForAll}`}>
        <UsersThree size={14} weight="regular" />
        {t("event.claimedForAll")}
      </span>
    );
  }
  if (assigned) {
    return (
      <span className={`${styles.assignReadPill} ${styles.assignReadPillAssigned}`}>
        <span
          className={styles.avatar}
          style={{ backgroundColor: avatarColor(assigned.name) }}
          aria-hidden
        >
          {initials(assigned.name)}
        </span>
        {t("event.claimedBy", { name: assigned.name })}
      </span>
    );
  }
  if (claimedAnonymously) {
    return (
      <span className={`${styles.assignReadPill} ${styles.assignReadPillAssigned}`}>
        <CheckCircle size={14} weight="regular" />
        {t("event.claimedTaken")}
      </span>
    );
  }
  return (
    <span className={styles.assignReadPill}>
      <PencilSimple size={14} weight="regular" />
      {t("event.claimOpen")}
    </span>
  );
}
